import { ApiError, api } from '@invisoutlet/shared/api';
import type { User } from '@invisoutlet/shared/types';
import { buildingsLabel, formatDate } from '@invisoutlet/shared/utils';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { type FormEvent, useState } from 'react';
import { InternalLayout } from '../components/internal-layout.js';

// ─── Types ────────────────────────────────────────────────────────────────────

interface AdminUserListItem extends User {
  entityName?: string;
}

interface AdminEntityOption {
  id: string;
  name: string;
}

interface EditUserBody {
  readOnly?: boolean;
  selfService?: boolean;
  buildings?: string[];
}

// ─── Sub-components ───────────────────────────────────────────────────────────

function RoleBadge({ role }: { role: User['role'] }) {
  return role === 'admin' ? (
    <span className="inline-flex items-center rounded-full bg-accent/10 px-2 py-0.5 text-xs font-semibold text-accent">
      Admin
    </span>
  ) : (
    <span className="inline-flex items-center rounded-full bg-bg-elevated px-2 py-0.5 text-xs font-medium text-text-secondary">
      User
    </span>
  );
}

function FlagBadge({ label, active }: { label: string; active: boolean }) {
  if (!active) return null;
  return (
    <span className="inline-flex items-center rounded-full bg-warning/10 px-2 py-0.5 text-xs font-medium text-warning">
      {label}
    </span>
  );
}

// ─── Edit User Modal ──────────────────────────────────────────────────────────

interface EditUserModalProps {
  user: AdminUserListItem;
  onClose: () => void;
  onSaved: () => void;
}

function EditUserModal({ user, onClose, onSaved }: EditUserModalProps) {
  const [readOnly, setReadOnly] = useState(user.readOnly);
  const [selfService, setSelfService] = useState(user.selfService);
  const [error, setError] = useState<string | null>(null);

  const mutation = useMutation({
    mutationFn: (body: EditUserBody) => api.patch<User>(`/api/admin/users/${user.uid}`, body),
    onSuccess: () => {
      onSaved();
      onClose();
    },
    onError: (err) => {
      setError(err instanceof ApiError ? err.message : 'Failed to update user.');
    },
  });

  function handleSubmit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    mutation.mutate({ readOnly, selfService });
  }

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="edit-user-title"
      className="fixed inset-0 z-50 flex items-center justify-center p-4"
    >
      <div className="absolute inset-0 bg-black/40" onClick={onClose} aria-hidden="true" />
      <div className="relative w-full max-w-md rounded-xl border border-border bg-bg-primary shadow-xl">
        <div className="flex items-center justify-between border-b border-border px-6 py-4">
          <div>
            <h2 id="edit-user-title" className="text-base font-semibold text-text-primary">
              Edit User
            </h2>
            <p className="text-xs text-text-secondary">
              {user.name} — {user.email}
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close"
            className="rounded-lg p-1 text-text-secondary hover:bg-bg-elevated hover:text-text-primary focus:outline-none focus:ring-2 focus:ring-accent"
          >
            <svg
              className="h-5 w-5"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              aria-hidden="true"
            >
              <path d="M18 6L6 18M6 6l12 12" />
            </svg>
          </button>
        </div>

        <form onSubmit={handleSubmit} noValidate className="p-6">
          <p className="mb-4 text-xs font-semibold uppercase tracking-wide text-text-secondary">
            Permission Flags
          </p>

          <div className="space-y-3">
            <label className="flex items-center justify-between rounded-lg border border-border bg-bg-surface p-3">
              <div>
                <p className="text-sm font-medium text-text-primary">Read Only</p>
                <p className="text-xs text-text-secondary">
                  View only — cannot acknowledge alerts, configure rules, or edit settings.
                </p>
              </div>
              <button
                type="button"
                role="switch"
                aria-checked={readOnly}
                onClick={() => setReadOnly((v) => !v)}
                className={[
                  'relative inline-flex h-5 w-9 shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors focus:outline-none focus:ring-2 focus:ring-accent focus:ring-offset-2',
                  readOnly ? 'bg-accent' : 'bg-bg-elevated',
                ].join(' ')}
              >
                <span
                  aria-hidden="true"
                  className={[
                    'pointer-events-none inline-block h-4 w-4 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out',
                    readOnly ? 'translate-x-4' : 'translate-x-0',
                  ].join(' ')}
                />
              </button>
            </label>

            <label className="flex items-center justify-between rounded-lg border border-border bg-bg-surface p-3">
              <div>
                <p className="text-sm font-medium text-text-primary">Self Service</p>
                <p className="text-xs text-text-secondary">
                  Can tag and provision devices (assign building, floor, room, name).
                </p>
              </div>
              <button
                type="button"
                role="switch"
                aria-checked={selfService}
                onClick={() => setSelfService((v) => !v)}
                className={[
                  'relative inline-flex h-5 w-9 shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors focus:outline-none focus:ring-2 focus:ring-accent focus:ring-offset-2',
                  selfService ? 'bg-accent' : 'bg-bg-elevated',
                ].join(' ')}
              >
                <span
                  aria-hidden="true"
                  className={[
                    'pointer-events-none inline-block h-4 w-4 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out',
                    selfService ? 'translate-x-4' : 'translate-x-0',
                  ].join(' ')}
                />
              </button>
            </label>
          </div>

          {error && (
            <p role="alert" className="mt-3 rounded-lg bg-danger/10 px-3 py-2 text-sm text-danger">
              {error}
            </p>
          )}

          <div className="mt-6 flex justify-end gap-3">
            <button
              type="button"
              onClick={onClose}
              className="rounded-lg border border-border px-4 py-2 text-sm font-medium text-text-secondary transition-colors hover:bg-bg-elevated focus:outline-none focus:ring-2 focus:ring-accent"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={mutation.isPending}
              className="rounded-lg bg-accent px-4 py-2 text-sm font-medium text-white transition-opacity hover:opacity-90 focus:outline-none focus:ring-2 focus:ring-accent/50 disabled:opacity-50"
            >
              {mutation.isPending ? 'Saving…' : 'Save Changes'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

// ─── Disable Confirm ──────────────────────────────────────────────────────────

interface DisableConfirmProps {
  userName: string;
  onConfirm: () => void;
  onCancel: () => void;
  isPending: boolean;
}

function DisableConfirm({ userName, onConfirm, onCancel, isPending }: DisableConfirmProps) {
  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="disable-user-title"
      className="fixed inset-0 z-50 flex items-center justify-center p-4"
    >
      <div className="absolute inset-0 bg-black/40" onClick={onCancel} aria-hidden="true" />
      <div className="relative w-full max-w-sm rounded-xl border border-border bg-bg-primary p-6 shadow-xl">
        <h2 id="disable-user-title" className="text-base font-semibold text-text-primary">
          Disable User Account
        </h2>
        <p className="mt-2 text-sm text-text-secondary">
          Are you sure you want to disable{' '}
          <span className="font-medium text-text-primary">{userName}</span>? They will no longer be
          able to sign in.
        </p>
        <div className="mt-5 flex justify-end gap-3">
          <button
            type="button"
            onClick={onCancel}
            className="rounded-lg border border-border px-4 py-2 text-sm font-medium text-text-secondary transition-colors hover:bg-bg-elevated focus:outline-none focus:ring-2 focus:ring-accent"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={onConfirm}
            disabled={isPending}
            className="rounded-lg bg-danger px-4 py-2 text-sm font-medium text-white transition-opacity hover:opacity-90 focus:outline-none focus:ring-2 focus:ring-danger/50 disabled:opacity-50"
          >
            {isPending ? 'Disabling…' : 'Disable'}
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── Page ─────────────────────────────────────────────────────────────────────

export default function UsersPage() {
  const queryClient = useQueryClient();
  const [search, setSearch] = useState('');
  const [entityFilter, setEntityFilter] = useState('');
  const [roleFilter, setRoleFilter] = useState('');
  const [editingUser, setEditingUser] = useState<AdminUserListItem | null>(null);
  const [disablingUser, setDisablingUser] = useState<AdminUserListItem | null>(null);
  const [resetSuccessId, setResetSuccessId] = useState<string | null>(null);

  // Entities for filter dropdown
  const { data: entitiesData } = useQuery({
    queryKey: ['admin-entities-options'],
    queryFn: () => api.get<AdminEntityOption[]>('/api/admin/entities?limit=200'),
  });
  const entityOptions: AdminEntityOption[] =
    (entitiesData?.data as AdminEntityOption[] | undefined) ?? [];

  // Users list
  const params = new URLSearchParams();
  if (entityFilter) params.set('entityId', entityFilter);
  if (roleFilter) params.set('role', roleFilter);
  params.set('limit', '100');

  const { data, isLoading, error } = useQuery({
    queryKey: ['admin-users', entityFilter, roleFilter],
    queryFn: () => api.get<AdminUserListItem[]>(`/api/admin/users?${params.toString()}`),
  });

  const allUsers: AdminUserListItem[] = (data?.data as AdminUserListItem[] | undefined) ?? [];

  // Client-side search filter (name / email)
  const users = search.trim()
    ? allUsers.filter(
        (u) =>
          u.name.toLowerCase().includes(search.toLowerCase()) ||
          u.email.toLowerCase().includes(search.toLowerCase()),
      )
    : allUsers;

  const disableMutation = useMutation({
    mutationFn: (userId: string) => api.delete(`/api/admin/users/${userId}`),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['admin-users'] });
      setDisablingUser(null);
    },
  });

  const resetPasswordMutation = useMutation({
    mutationFn: (userId: string) => api.post(`/api/admin/users/${userId}/reset-password`, {}),
    onSuccess: (_data, userId) => {
      setResetSuccessId(userId);
      setTimeout(() => setResetSuccessId(null), 3000);
    },
  });

  return (
    <InternalLayout title="Users">
      <div className="flex flex-col gap-6">
        {/* Page header */}
        <div>
          <h2 className="text-xl font-semibold text-text-primary">User Management</h2>
          <p className="mt-0.5 text-sm text-text-secondary">
            {isLoading ? 'Loading…' : `${users.length} user${users.length !== 1 ? 's' : ''}`}
          </p>
        </div>

        {/* Filters row */}
        <div className="flex flex-wrap items-center gap-3">
          {/* Search */}
          <div className="relative flex-1" style={{ minWidth: '200px', maxWidth: '320px' }}>
            <svg
              className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-text-secondary"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              aria-hidden="true"
            >
              <circle cx="11" cy="11" r="8" />
              <path d="M21 21l-4.35-4.35" />
            </svg>
            <input
              type="search"
              placeholder="Search by name or email…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="w-full rounded-lg border border-border bg-bg-surface py-2 pl-9 pr-4 text-sm text-text-primary outline-none transition-colors placeholder:text-text-secondary focus:border-accent focus:ring-2 focus:ring-accent/20"
            />
          </div>

          {/* Entity filter */}
          <select
            value={entityFilter}
            onChange={(e) => setEntityFilter(e.target.value)}
            aria-label="Filter by entity"
            className="rounded-lg border border-border bg-bg-surface px-3 py-2 text-sm text-text-primary outline-none transition-colors focus:border-accent focus:ring-2 focus:ring-accent/20"
          >
            <option value="">All entities</option>
            {entityOptions.map((e) => (
              <option key={e.id} value={e.id}>
                {e.name}
              </option>
            ))}
          </select>

          {/* Role filter */}
          <select
            value={roleFilter}
            onChange={(e) => setRoleFilter(e.target.value)}
            aria-label="Filter by role"
            className="rounded-lg border border-border bg-bg-surface px-3 py-2 text-sm text-text-primary outline-none transition-colors focus:border-accent focus:ring-2 focus:ring-accent/20"
          >
            <option value="">All roles</option>
            <option value="user">User</option>
            <option value="admin">Admin</option>
          </select>
        </div>

        {/* Reset password success toast */}
        {resetSuccessId && (
          <div role="status" className="rounded-lg bg-success/10 px-4 py-3 text-sm text-success">
            Password reset email sent successfully.
          </div>
        )}

        {/* Table */}
        {isLoading ? (
          <div className="flex items-center justify-center py-20 text-sm text-text-secondary">
            Loading users…
          </div>
        ) : error ? (
          <div role="alert" className="rounded-lg bg-danger/10 px-4 py-3 text-sm text-danger">
            {error instanceof ApiError ? error.message : 'Failed to load users. Please try again.'}
          </div>
        ) : users.length === 0 ? (
          <div className="flex flex-col items-center justify-center rounded-xl border border-border bg-bg-surface py-20 text-center">
            <svg
              className="mb-3 h-10 w-10 text-text-secondary"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="1.25"
              aria-hidden="true"
            >
              <path d="M17 21v-2a4 4 0 00-4-4H5a4 4 0 00-4 4v2M9 11a4 4 0 100-8 4 4 0 000 8zM23 21v-2a4 4 0 00-3-3.87M16 3.13a4 4 0 010 7.75" />
            </svg>
            <p className="text-sm font-medium text-text-primary">No users found</p>
            <p className="mt-1 text-xs text-text-secondary">
              {search || entityFilter || roleFilter
                ? 'Try adjusting the filters.'
                : 'No users exist yet.'}
            </p>
          </div>
        ) : (
          <div className="overflow-hidden rounded-xl border border-border">
            <table className="min-w-full divide-y divide-border text-sm">
              <thead className="bg-bg-surface">
                <tr>
                  <th
                    scope="col"
                    className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-text-secondary"
                  >
                    Name
                  </th>
                  <th
                    scope="col"
                    className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-text-secondary"
                  >
                    Entity
                  </th>
                  <th
                    scope="col"
                    className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-text-secondary"
                  >
                    Role
                  </th>
                  <th
                    scope="col"
                    className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-text-secondary"
                  >
                    Flags
                  </th>
                  <th
                    scope="col"
                    className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-text-secondary"
                  >
                    Building Access
                  </th>
                  <th
                    scope="col"
                    className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-text-secondary"
                  >
                    Created
                  </th>
                  <th
                    scope="col"
                    className="px-4 py-3 text-right text-xs font-semibold uppercase tracking-wide text-text-secondary"
                  >
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border bg-bg-primary">
                {users.map((user) => (
                  <tr key={user.uid} className="transition-colors hover:bg-bg-surface">
                    <td className="px-4 py-3">
                      <p className="font-medium text-text-primary">{user.name}</p>
                      <p className="text-xs text-text-secondary">{user.email}</p>
                    </td>
                    <td className="px-4 py-3 text-text-secondary">
                      {user.entityName ?? user.entityId ?? (
                        <span className="text-text-secondary italic">—</span>
                      )}
                    </td>
                    <td className="px-4 py-3">
                      <RoleBadge role={user.role} />
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex flex-wrap gap-1">
                        <FlagBadge label="Read Only" active={user.readOnly} />
                        <FlagBadge label="Self Service" active={user.selfService} />
                        {!user.readOnly && !user.selfService && (
                          <span className="text-xs text-text-secondary">Default</span>
                        )}
                      </div>
                    </td>
                    <td className="px-4 py-3 text-xs text-text-secondary">
                      {buildingsLabel(user.buildings)}
                    </td>
                    <td className="px-4 py-3 text-text-secondary">{formatDate(user.createdAt)}</td>
                    <td className="px-4 py-3">
                      <div className="flex items-center justify-end gap-1">
                        <button
                          type="button"
                          onClick={() => setEditingUser(user)}
                          className="rounded-md px-2.5 py-1 text-xs font-medium text-accent transition-colors hover:bg-accent/10 focus:outline-none focus:ring-2 focus:ring-accent"
                        >
                          Edit
                        </button>
                        <button
                          type="button"
                          onClick={() => resetPasswordMutation.mutate(user.uid)}
                          disabled={
                            resetPasswordMutation.isPending &&
                            resetPasswordMutation.variables === user.uid
                          }
                          className="rounded-md px-2.5 py-1 text-xs font-medium text-text-secondary transition-colors hover:bg-bg-elevated hover:text-text-primary focus:outline-none focus:ring-2 focus:ring-accent disabled:opacity-40"
                        >
                          Reset PW
                        </button>
                        <button
                          type="button"
                          onClick={() => setDisablingUser(user)}
                          className="rounded-md px-2.5 py-1 text-xs font-medium text-danger transition-colors hover:bg-danger/10 focus:outline-none focus:ring-2 focus:ring-danger"
                        >
                          Disable
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {editingUser && (
        <EditUserModal
          user={editingUser}
          onClose={() => setEditingUser(null)}
          onSaved={() => void queryClient.invalidateQueries({ queryKey: ['admin-users'] })}
        />
      )}

      {disablingUser && (
        <DisableConfirm
          userName={disablingUser.name}
          isPending={disableMutation.isPending}
          onConfirm={() => disableMutation.mutate(disablingUser.uid)}
          onCancel={() => setDisablingUser(null)}
        />
      )}
    </InternalLayout>
  );
}
